// Formatage texte léger pour les publications (barre d'outils sur le <textarea>).
// Repères Markdown insérés à la sélection : **gras**, *italique*, __souligné__, « - » liste, « > » citation.
// Le rendu réel (React) est fait par components/RichText.jsx après publication.

const INLINE = {
  bold: { open: "**", close: "**" },
  italic: { open: "*", close: "*" },
  underline: { open: "__", close: "__" },
};

// Règles de lecture (ordre = priorité à position égale : « ** » avant « * »). Partagées avec le rendu (RichText.jsx)
// et l'éditeur (lib/richDoc.js).
export const INLINE_RULES = [
  { kind: "bold", open: "**", close: "**" },
  { kind: "underline", open: "__", close: "__" },
  { kind: "italic", open: "*", close: "*" },
];

// Premier repère ouvrant/fermant valide dans `text` : { rule, start, close } ou null. Un repère ne touche jamais une
// espace côté intérieur. Lot 778 : le repère fermant glisse au bout d'une suite d'astérisques (« ***x*** » = gras+italique).
export function findInlineMatch(text) {
  let best = null;
  for (const rule of INLINE_RULES) {
    const ol = rule.open.length;
    let s = text.indexOf(rule.open);
    while (s !== -1) {
      const after = text[s + ol];
      if (after && after !== " " && after !== "\n") {
        let c = text.indexOf(rule.close, s + ol + 1);
        if (c !== -1 && text[c - 1] && text[c - 1] !== " ") {
          while (rule.close[0] === "*" && text[c + rule.close.length] === "*") c += 1;
          if (!best || s < best.start) best = { rule, start: s, close: c };
          break;
        }
      }
      s = text.indexOf(rule.open, s + 1);
    }
  }
  return best;
}

// Mot sous le curseur (sans sélection) : étendu à gauche / droite jusqu'à un espace ou un saut de ligne.
const wordAt = (src, pos) => {
  let a = pos, b = pos;
  while (a > 0 && !/\s/.test(src[a - 1])) a -= 1;
  while (b < src.length && !/\s/.test(src[b])) b += 1;
  return [a, b];
};

// Applique un format à la sélection d'un <textarea>. Renvoie { value, selStart, selEnd, hint? }.
// Sans sélection : le mot sous le curseur ; ni l'un ni l'autre → rien n'est inséré (hint:true → le composeur explique).
// Plus jamais de texte d'exemple (« texte souligné ») : sur téléphone il restait dans la publication (lot 777).
export function applyRichFormat(el, kind, value) {
  const src = String(value ?? el?.value ?? "");
  let start = typeof el?.selectionStart === "number" ? el.selectionStart : src.length;
  let end = typeof el?.selectionEnd === "number" ? el.selectionEnd : src.length;

  if (kind === "list" || kind === "quote") {
    const prefix = kind === "list" ? "- " : "> ";
    const lineStart = src.lastIndexOf("\n", Math.max(0, start - 1)) + 1;
    let lineEnd = src.indexOf("\n", end);
    if (lineEnd === -1) lineEnd = src.length;
    const block = src.slice(lineStart, lineEnd);
    const lines = block.split("\n");
    const nonEmpty = lines.filter((l) => l.trim() !== "");
    const allPrefixed = nonEmpty.length > 0 && nonEmpty.every((l) => l.startsWith(prefix));
    const newLines = lines.map((l) => {
      if (l.trim() === "") return l;
      return allPrefixed ? l.slice(prefix.length) : prefix + l;
    });
    const nb = newLines.join("\n");
    return { value: src.slice(0, lineStart) + nb + src.slice(lineEnd), selStart: lineStart, selEnd: lineStart + nb.length };
  }

  const rule = INLINE[kind];
  if (!rule) return { value: src, selStart: start, selEnd: end };
  if (start === end) [start, end] = wordAt(src, start);
  const sel = src.slice(start, end);
  if (!sel) return { value: src, selStart: start, selEnd: end, hint: true };
  // Déjà entouré → on enlève le format (toggle).
  if (sel.length >= rule.open.length + rule.close.length && sel.startsWith(rule.open) && sel.endsWith(rule.close)) {
    const inner = sel.slice(rule.open.length, sel.length - rule.close.length);
    return { value: src.slice(0, start) + inner + src.slice(end), selStart: start, selEnd: start + inner.length };
  }
  const wrapped = rule.open + sel + rule.close;
  return {
    value: src.slice(0, start) + wrapped + src.slice(end),
    selStart: start + rule.open.length,
    selEnd: start + rule.open.length + sel.length,
  };
}

// Retire les repères de mise en forme (pour un aperçu texte brut : notifications, recherche…).
export function stripRichMarkers(text) {
  return String(text || "")
    .replace(/\*\*([^\n]+?)\*\*/g, "$1")
    .replace(/__([^\n]+?)__/g, "$1")
    .replace(/\*([^\s*][^\n]*?)\*/g, "$1")
    .replace(/^\s*[->]\s+/gm, "");
}
